/**
 * Print preview window — opens over the current screen when a document is
 * printed (invoices, orders, challans, GRN, returns, vouchers…). Shows the
 * page exactly as it will print, with zoom, Print, Download PDF and — when the
 * company has WhatsApp — Send on WhatsApp.
 *
 * Mounted ONCE in App.js. printDocument() hands it the print page through
 * utils/printPreviewBus; if this window is not mounted, printing falls back to
 * the old new-tab print, so printing never stops working.
 *
 * Firebase cost: nothing. The WhatsApp button looks the customer / vendor up in
 * the list already saved on this device (only when WhatsApp is switched on).
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { FiPrinter, FiDownload, FiX, FiZoomIn, FiZoomOut, FiLoader } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { registerPrintPreviewHost, printHtmlInNewWindow } from '../utils/printPreviewBus';
import { buildPdfFromPreview, downloadBlob, waitForImages, PAPER_MM, PX_PER_MM } from '../utils/printPdf';
import { DEFAULT_TEMPLATES } from '../utils/whatsappTemplates';
import { getCustomersCached, getVendorsCached } from '../utils/masterDataStore';
import SendWhatsAppButton from './SendWhatsAppButton';

const ZOOM_MIN = 0.4;
const ZOOM_MAX = 2;

// Phones / tablets: printing from inside a frame is unreliable there (some
// print the whole app), so Print uses the old new-tab print on those devices.
const isTouchDevice = () => {
  try {
    const ua = navigator.userAgent || '';
    return /Android|iPhone|iPad|iPod|Mobile/i.test(ua)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  } catch (e) { return false; }
};

// The print page + a screen-only style: white sheet, the printer's margins
// around it, no inner scrollbar. Nothing here changes how it prints.
const buildPreviewHtml = (html, pageMargin) => {
  const screenCss = `<style id="amal-print-preview">@media screen{html,body{background:#fff}html{overflow:hidden}${
    pageMargin ? `body{padding:${pageMargin} !important}` : ''}}</style>`;
  return html.includes('</head>') ? html.replace('</head>', `${screenCss}</head>`) : screenCss + html;
};

const PrintPreviewModal = ({ job, onClose }) => {
  const { activeCompany, isModuleEnabled } = useAuth();
  const iframeRef = useRef(null);
  const areaRef = useRef(null);
  const paper = PAPER_MM[job.paperSize] || PAPER_MM.A4;
  const pageWpx = Math.round(paper.w * PX_PER_MM);
  const pageHpx = Math.round(paper.h * PX_PER_MM);

  const [zoom, setZoom] = useState(1);
  const [contentH, setContentH] = useState(pageHpx);
  const [ready, setReady] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [note, setNote] = useState('');
  const [party, setParty] = useState(null);
  const readyRef = useRef(false);
  useEffect(() => { readyRef.current = ready; }, [ready]);

  const srcDoc = useMemo(() => buildPreviewHtml(job.html, job.pageMargin), [job.html, job.pageMargin]);

  // Start at a zoom that fits the window width (phones), else 100%.
  useEffect(() => {
    const area = areaRef.current;
    const avail = area ? area.clientWidth - 32 : window.innerWidth - 32;
    if (avail > 0 && avail < pageWpx) setZoom(Math.max(ZOOM_MIN, Math.floor((avail / pageWpx) * 20) / 20));
  }, [pageWpx]);

  // Stop the page behind from scrolling while the preview is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const measure = useCallback(() => {
    const doc = iframeRef.current && iframeRef.current.contentDocument;
    if (!doc || !doc.documentElement) return;
    const h = Math.max(doc.documentElement.scrollHeight, doc.body ? doc.body.scrollHeight : 0);
    setContentH(Math.max(pageHpx, Math.ceil(h)));
  }, [pageHpx]);

  const handleLoad = useCallback(() => {
    const doc = iframeRef.current && iframeRef.current.contentDocument;
    if (!doc) return;
    measure();
    waitForImages(doc).then(() => { measure(); setReady(true); });
  }, [measure]);

  const doPrint = useCallback(() => {
    if (!readyRef.current) return; // logo / letterhead still loading (max 8 s)
    if (isTouchDevice()) {
      if (!printHtmlInNewWindow(job.html)) alert('Please allow pop-ups for this site to print.');
      return;
    }
    const win = iframeRef.current && iframeRef.current.contentWindow;
    try {
      win.focus();
      win.print();
    } catch (e) {
      if (!printHtmlInNewWindow(job.html)) alert('Please allow pop-ups for this site to print.');
    }
  }, [job.html]);

  const doPdf = useCallback(async () => {
    if (pdfBusy) return;
    const doc = iframeRef.current && iframeRef.current.contentDocument;
    if (!doc) return;
    setPdfBusy(true);
    setNote('');
    try {
      const { blob, missingImages } = await buildPdfFromPreview(doc, {
        paperSize: job.paperSize, letterhead: job.letterhead, brand: job.brand || null,
      });
      downloadBlob(blob, job.fileName);
      if (missingImages > 0) {
        setNote('A picture (logo / letterhead) could not be added to the PDF. For an exact copy use Print → "Save as PDF".');
      }
    } catch (e) {
      console.error('[print preview] PDF failed:', e);
      setNote('The PDF could not be made here. Opening Print — choose "Save as PDF" as the printer.');
      doPrint();
    } finally {
      setPdfBusy(false);
    }
  }, [pdfBusy, job.paperSize, job.letterhead, job.brand, job.fileName, doPrint]);

  // Esc closes; Ctrl/Cmd+P prints this document (also while the page has focus).
  // While the preview is open, keys never reach the screen behind it — so Esc
  // doesn't also close the invoice underneath and Ctrl+S can't save it.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      else if ((e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 'P')) { e.preventDefault(); doPrint(); }
      e.stopPropagation();
    };
    window.addEventListener('keydown', onKey, true);
    let frameWin = null;
    const iframe = iframeRef.current;
    const attach = () => {
      try { frameWin = iframe && iframe.contentWindow; if (frameWin) frameWin.addEventListener('keydown', onKey); } catch (e) { frameWin = null; }
    };
    if (iframe) iframe.addEventListener('load', attach);
    attach();
    return () => {
      window.removeEventListener('keydown', onKey, true);
      if (iframe) iframe.removeEventListener('load', attach);
      try { if (frameWin) frameWin.removeEventListener('keydown', onKey); } catch (e) { /* ignore */ }
    };
  }, [onClose, doPrint]);

  // WhatsApp: only for documents that have a WhatsApp message, only when the
  // company has WhatsApp switched on, and only for a saved customer / vendor.
  const waTemplate = useMemo(() => DEFAULT_TEMPLATES.find(
    (t) => t.key === job.docTypeKey && (t.recipient === 'customer' || t.recipient === 'vendor'),
  ), [job.docTypeKey]);
  const waOn = !!(waTemplate && job.data && job.data.partyId && activeCompany?.id
    && typeof isModuleEnabled === 'function' && isModuleEnabled('whatsapp_connect'));

  useEffect(() => {
    let alive = true;
    if (!waOn) { setParty(null); return undefined; }
    const load = waTemplate.recipient === 'vendor' ? getVendorsCached : getCustomersCached;
    load(activeCompany.id)
      .then((rows) => { if (alive) setParty((rows || []).find((r) => r.id === job.data.partyId) || null); })
      .catch(() => { if (alive) setParty(null); });
    return () => { alive = false; };
  }, [waOn, waTemplate, activeCompany, job.data]);

  const zoomBy = (d) => setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round((z + d) * 10) / 10)));

  return (
    <Overlay>
      <Panel role="dialog" aria-label={job.title}>
        <Bar>
          <Title title={job.title}>{job.title}</Title>
          <Tools>
            <IconBtn onClick={() => zoomBy(-0.1)} disabled={zoom <= ZOOM_MIN} title="Zoom out"><FiZoomOut /></IconBtn>
            <ZoomVal>{Math.round(zoom * 100)}%</ZoomVal>
            <IconBtn onClick={() => zoomBy(0.1)} disabled={zoom >= ZOOM_MAX} title="Zoom in"><FiZoomIn /></IconBtn>
            <Sep />
            {waOn && party && (
              <SendWhatsAppButton templateKey={waTemplate.key} doc={job.data} party={party} variant="button" label="WhatsApp" />
            )}
            <Btn onClick={doPrint} disabled={!ready} title="Print (Ctrl+P)"><FiPrinter /> <span>Print</span></Btn>
            <Btn $primary onClick={doPdf} disabled={pdfBusy || !ready} title="Download PDF">
              {pdfBusy ? <FiLoader className="spin" /> : <FiDownload />} <span>{pdfBusy ? 'Preparing…' : 'Download PDF'}</span>
            </Btn>
            <IconBtn onClick={onClose} title="Close (Esc)"><FiX size={20} /></IconBtn>
          </Tools>
        </Bar>
        {note && <Note>{note}</Note>}
        <Area ref={areaRef}>
          <Sheet style={{ width: pageWpx * zoom, height: contentH * zoom }}>
            <iframe
              ref={iframeRef}
              title={job.title}
              srcDoc={srcDoc}
              onLoad={handleLoad}
              style={{
                width: pageWpx, height: contentH, border: 0, display: 'block', background: '#fff',
                transform: `scale(${zoom})`, transformOrigin: 'top left',
              }}
            />
          </Sheet>
        </Area>
      </Panel>
    </Overlay>
  );
};

const PrintPreviewHost = () => {
  const [job, setJob] = useState(null);
  useEffect(() => registerPrintPreviewHost((payload) => setJob({ ...payload, _k: Date.now() })), []);
  const close = useCallback(() => setJob(null), []);
  if (!job) return null;
  return <PrintPreviewModal key={job._k} job={job} onClose={close} />;
};

// ── styles ───────────────────────────────────────────────────────────────────
const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 200000;
  background: rgba(15, 17, 21, 0.55);
  display: flex; align-items: stretch; justify-content: center;
  padding: 20px;
  @media (max-width: 700px) { padding: 0; }
`;
const Panel = styled.div`
  width: 100%; max-width: 1280px;
  display: flex; flex-direction: column;
  background: #fff; border-radius: 12px; overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  @media (max-width: 700px) { border-radius: 0; }
`;
const Bar = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 12px 18px; border-bottom: 1px solid #e5e7eb; background: #fff;
  @media (max-width: 700px) { padding: 10px; flex-wrap: wrap; }
`;
const Title = styled.div`
  flex: 1; min-width: 0; font-size: 18px; font-weight: 700; color: #1a1a1a;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  @media (max-width: 700px) { font-size: 15px; flex-basis: 100%; }
`;
const Tools = styled.div`
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap; justify-content: flex-end;
`;
const ZoomVal = styled.span`min-width: 46px; text-align: center; font-size: 14px; font-weight: 600; color: #333;`;
const Sep = styled.span`width: 1px; height: 24px; background: #e5e7eb; margin: 0 4px;`;
const IconBtn = styled.button`
  display: inline-flex; align-items: center; justify-content: center;
  width: 36px; height: 36px; border-radius: 8px; border: none; background: transparent;
  color: #444; cursor: pointer; font-size: 18px;
  &:hover:not(:disabled) { background: #f1f3f5; }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
`;
const Btn = styled.button`
  display: inline-flex; align-items: center; gap: 8px;
  height: 38px; padding: 0 16px; border-radius: 8px; font-size: 14px; font-weight: 600;
  cursor: pointer; white-space: nowrap;
  border: 1px solid ${(p) => (p.$primary ? '#800020' : '#d0d5dd')};
  background: ${(p) => (p.$primary ? '#800020' : '#fff')};
  color: ${(p) => (p.$primary ? '#fff' : '#1a1a1a')};
  &:hover:not(:disabled) { opacity: 0.92; background: ${(p) => (p.$primary ? '#800020' : '#f8f9fa')}; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  .spin { animation: amalPpSpin 1s linear infinite; }
  @keyframes amalPpSpin { to { transform: rotate(360deg); } }
  @media (max-width: 480px) { padding: 0 12px; span { display: none; } }
`;
const Note = styled.div`
  padding: 8px 18px; font-size: 13px; color: #8a5a00; background: #fff8e6; border-bottom: 1px solid #f3e2b3;
`;
const Area = styled.div`
  flex: 1; overflow: auto; background: #eef0f3; padding: 24px 16px;
`;
const Sheet = styled.div`
  margin: 0 auto; overflow: hidden; background: #fff;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.18);
`;

export default PrintPreviewHost;
